import RateElement from './rateElement';
import PropTypes from 'prop-types';
import React from 'react';

import { useTranslation } from 'react-i18next';
import { Table, TableCellDropdown, DropdownItem, ToastContext } from '@weezevent/nacre';

import GroupModal from './groupModal';
import PositionInput from './position-input';
import StyleRM from '../rates/components/rateManager.module.css';

import { filterCells } from './utils';
import { DeleteModal } from './components/action/deleteModal';
import { useSegment } from '../../components/tracking/trackingProvider';
import { getPipeTranslationByLocale, convertPyvarToMenhirLocale } from '../../components/utils/pipeTranslateConvert';

const { Tr: TableRow, Td: TableCell } = Table;

function GroupTableRow({
    group,
    rates,
    tableCells,
    isSelected,
    onGroupSelected,
    setGroupContainerModal,
    setGroup,
    event,
    orgaWithChart,
    hideEmptyGroup,
    groups,
    onPositionChanged,
    onToggle,
    mode
}) {
    const { t } = useTranslation();
    const { track } = useSegment();
    let cells = tableCells
        .filter(column => filterCells(event, column, orgaWithChart))
        .map((column, index) => (
            <TableCell key={`group-cell-${index}`} className="number-of-rates">
                {column.renderGroup(group, onToggle)}
            </TableCell>
        ));
    const isGroupAndEmpty = React.useMemo(() => {
        if (!group) {
            return false;
        }
        const subgroupsIds = groups.filter(g => g.parent === group.pk).map(gr => gr.pk);
        return rates.filter(rate => [...subgroupsIds, group.pk].includes(rate.group)).length === 0;
    }, [rates, groups, group]);

    // We don't want to allow the deletion of a rate group with children rate groups (that's buggy).
    const hasSubGroups = React.useMemo(() => !!groups.find(g => g.parent === group.pk), [group, groups]);

    // when "hideEmptyGroups" is set, we hide groups with no rate but some child rate group
    if (hideEmptyGroup && isGroupAndEmpty) {
        return null;
    }

    return (
        <TableRow
            className={StyleRM[group.parent ? 'subgroup-table-row' : 'group-table-row']}
            active={isSelected}
            onSelected={(event, data) => onGroupSelected({ event, data }, group, 'group')}
        >
            {cells}
            {mode === 'resale' && <TableCell />}
            {mode === 'rate-list' && (
                <>
                    <TableCellDropdown
                        onOpen={() => {
                            track('ACTION > CELL ACTIONS', { 'Page Title': 'TICKET TYPES' });
                        }}
                    >
                        <DropdownItem
                            item={<span>{t('ticket.rates.modifyGroup')}</span>}
                            onClick={() => {
                                setGroupContainerModal(true);
                                track('ACTION > EDIT GROUP', { 'Page Title': 'TICKET TYPES' });
                            }}
                        />
                        <DropdownItem.Line />
                        {isGroupAndEmpty && !hasSubGroups ? (
                            <DropdownItem
                                item={<span>{t('ticket.common.action.delete')}</span>}
                                onClick={() => {
                                    setGroup(group);
                                    track('ACTION > DELETE GROUP', { 'Page Title': 'TICKET TYPES' });
                                }}
                            />
                        ) : (
                            <DropdownItem disabled item={<span>{t('ticket.common.action.delete')}</span>} />
                        )}
                    </TableCellDropdown>
                    {!event && (
                        <TableCell>
                            <PositionInput
                                position={group.display_order}
                                onUp={() => {
                                    onPositionChanged(group, group.display_order + 1);
                                }}
                                onDown={() => {
                                    onPositionChanged(group, group.display_order - 1);
                                }}
                                min={0}
                                max={groups.length}
                            />
                        </TableCell>
                    )}
                </>
            )}
        </TableRow>
    );
}

GroupTableRow.defaultProps = {
    event: null,
    orgaWithChart: false,
    groups: [],
    onPositionChanged: () => {}
};

GroupTableRow.propTypes = {
    group: PropTypes.object.isRequired,
    rates: PropTypes.array.isRequired,
    tableCells: PropTypes.array.isRequired,
    isSelected: PropTypes.bool.isRequired,
    onGroupSelected: PropTypes.func.isRequired,
    setGroupContainerModal: PropTypes.func.isRequired,
    setGroup: PropTypes.func.isRequired,
    event: PropTypes.object,
    orgaWithChart: PropTypes.bool,
    hideEmptyGroup: PropTypes.bool,
    groups: PropTypes.array,
    onPositionChanged: PropTypes.func
};

export default function RatesGroup({
    tableCells,
    event,
    organizationId,
    group,
    rates,
    onRateSelected,
    selectedRates,
    reloadList,
    inventories,
    categories,
    onGroupSelected,
    isSelected,
    groups,
    GroupClass,
    RateClass,
    orgaWithChart,
    hideEmptyGroup = false,
    onToggleGroup = () => {},
    mode,
    getQuotaTakenRate = () => {},
    quotaTakenByGroup = {},
    settings = {},
    customActions = null,
    onRowClick = null
}) {
    const { t, i18n } = useTranslation();
    const [showGroupContainerModal, setGroupContainerModal] = React.useState(false);
    const [groupToDelete, setGroup] = React.useState(null);
    const toast = React.useContext(ToastContext);

    const deleteObject = React.useCallback(() => {
        group
            ?.delete({
                organizationId,
                eventId: event ? event.id : null
            })
            .then(() => {
                //handle display_group
                let editedGroups = [];
                let tmpGroups = groups.filter(fgroup => fgroup.pk !== group.pk);
                tmpGroups.forEach((fgroup, i) => {
                    //Set the new display order if different
                    if (group.display_order < fgroup.display_order) {
                        fgroup.display_order -= 1;
                        fgroup.translations = { name: {} };
                        editedGroups.push(fgroup);
                    }
                });
                GroupClass.save(editedGroups, {
                    organizationId,
                    eventId: event ? event.id : null
                }).then(() => {
                    toast.success(t(`ticket.rates.deleteGroupSuccess`));
                    reloadList();
                });
            })
            .catch(e => {
                toast.error(t(`ticket.rates.deleteError`));
            })
            .finally(() => {
                setGroup(null);
            });
    }, [groupToDelete, groups, group, event, organizationId, setGroup, reloadList, GroupClass]);

    const setElementOrder = React.useCallback(
        (element, position) => {
            // Sort rates
            const isRate = element instanceof RateClass;
            const elements_to_sort = isRate ? rates : groups;
            const ClassModel = isRate ? RateClass : GroupClass;
            let sorted_elements = elements_to_sort.sort((e1, e2) => e1.display_order - e2.display_order);

            // Reinitialize display orders
            sorted_elements.forEach((r, i) => (r.display_order = i));

            // shift all needed elements
            let elements_to_save = [element];

            // TODO: uncomment when we fix the translation problem in
            // order to change all display_orders simultaneously
            let index = sorted_elements.findIndex(e => e.pk === element.pk);
            if (position < index) {
                let offset = element.display_order - position;
                for (let i = position; i < offset + position; i++) {
                    if (!sorted_elements[i]) {
                        continue;
                    }
                    sorted_elements[i].display_order++;
                    elements_to_save.push(sorted_elements[i]);
                }
            } else {
                for (let i = element.display_order + 1; i < position; i++) {
                    if (!sorted_elements[i]) {
                        continue;
                    }
                    sorted_elements[i].display_order--;
                    elements_to_save.push(sorted_elements[i]);
                }
            }

            element.display_order = position;
            ClassModel.save(elements_to_save, {
                organizationId: organizationId,
                eventId: event ? event.pk : undefined
            })
                .then(() => {
                    reloadList();
                    toast.success(t(`ticket.rates.${isRate ? 'editSuccess' : 'editGroupSuccess'}`));
                })
                .catch(() => {
                    toast.error(t(`ticket.rates.${isRate ? 'editError' : 'editGroupError'}`));
                });
        },
        [rates, groups, GroupClass, RateClass]
    );

    const renderRatesElement = React.useCallback(() => {
        if (group && !group.showRates) {
            return null;
        }
        return rates
            .filter(rate => rate.group === (group ? group.pk : null))
            .map(r => {
                r.name =
                    getPipeTranslationByLocale(r.name, convertPyvarToMenhirLocale(i18n.language)) ||
                    getPipeTranslationByLocale(r.name, convertPyvarToMenhirLocale(event?.default_language)) ||
                    r.name;
                return r;
            })
            .map(element => {
                let selected = !!selectedRates.find(srate => srate.pk === element.pk);
                return (
                    <RateElement
                        key={element.pk}
                        tableCells={tableCells}
                        rate={element}
                        event={event}
                        isChecked={selected}
                        onSelected={onRateSelected}
                        organizationId={organizationId}
                        reloadList={reloadList}
                        inventories={inventories}
                        categories={categories}
                        group={group}
                        groups={groups}
                        rates={rates}
                        GroupClass={GroupClass}
                        RateClass={RateClass}
                        orgaWithChart={orgaWithChart}
                        onPositionChanged={setElementOrder}
                        mode={mode}
                        getQuotaTakenRate={getQuotaTakenRate}
                        customActions={customActions}
                        onClick={onRowClick}
                    />
                );
            });
    }, [selectedRates, rates, group, event, i18n]);

    const showRateSwitcher = React.useCallback(
        group => {
            let groupIndex = groups.findIndex(grp => grp.pk === group.pk);
            let switcher = group.showRates;

            let copyGroup = [...groups];
            copyGroup[groupIndex].showRates = !switcher;
            return onToggleGroup(copyGroup);
        },
        [groups, onToggleGroup]
    );

    if (group) {
        const parentShowRates = group.getAttributeFromParent('showRates', true);
        if (parentShowRates === false) {
            return null;
        }
    }

    return (
        <React.Fragment>
            <DeleteModal
                objectsToDelete={groupToDelete}
                show={groupToDelete}
                onClose={() => setGroup(null)}
                onSubmit={deleteObject}
                rates={rates}
                labelTitle={t('ticket.rates.deleteGroupTitle')}
                labelBody={t('ticket.rates.deleteGroupContent', { name: groupToDelete?.name })}
            />
            {showGroupContainerModal && (
                <GroupModal
                    group={group}
                    groups={groups}
                    groupId={group ? group.pk : -1}
                    event={event}
                    showModal={showGroupContainerModal}
                    onClose={() => setGroupContainerModal(false)}
                    onAccept={() => {
                        reloadList();
                        setGroupContainerModal(false);
                    }}
                    GroupClass={GroupClass}
                    quotaTakenByGroup={quotaTakenByGroup}
                    rates={rates}
                    settings={settings}
                />
            )}
            {group && (
                <GroupTableRow
                    hideEmptyGroup={hideEmptyGroup}
                    group={group}
                    rates={rates}
                    tableCells={tableCells}
                    isSelected={isSelected}
                    onGroupSelected={onGroupSelected}
                    setGroupContainerModal={setGroupContainerModal}
                    setGroup={setGroup}
                    event={event}
                    orgaWithChart={orgaWithChart}
                    groups={groups}
                    onPositionChanged={setElementOrder}
                    onToggle={showRateSwitcher}
                    mode={mode}
                />
            )}
            {renderRatesElement()}
        </React.Fragment>
    );
}

RatesGroup.defaultProps = {
    event: null,
    group: null,
    isSelected: null,
    groups: [],
    orgaWithChart: false
};

RatesGroup.propTypes = {
    event: PropTypes.object,
    organizationId: PropTypes.number.isRequired,
    group: PropTypes.object,
    rates: PropTypes.array.isRequired,
    onRateSelected: PropTypes.func.isRequired,
    onGroupSelected: PropTypes.func.isRequired,
    selectedRates: PropTypes.array.isRequired,
    reloadList: PropTypes.func.isRequired,
    categories: PropTypes.array,
    inventories: PropTypes.array,
    isSelected: PropTypes.bool,
    groups: PropTypes.array,
    RateClass: PropTypes.func.isRequired,
    GroupClass: PropTypes.func.isRequired,
    tableCells: PropTypes.array.isRequired,
    orgaWithChart: PropTypes.bool,
    hideEmptyGroup: PropTypes.bool,
    quotaTakenByGroup: PropTypes.object,
    settings: PropTypes.object
};
